import type { AuthenticationData, AuthenticationRequest, AuthenticationResponse } from '../../auth/types.ts';
import { broadcast, error, isLocalSource, isRemoteSource, token } from '../../common/messages.ts';
import { exceptionToReason, reason } from '../../reason.ts';
import {
    failure,
    globalAlreadySeen,
    globalAuthenticationFailure,
    globalDomainUri,
    globalInvalidDomain,
    globalInvalidPeer,
    globalUnhandledMessage
} from './constants.ts';
import type { Domain, HandleReturnType, StateToMessagesReturnType } from '../../domain.ts';
import type {
    CommandType,
    DomainType,
    GlobalRequestType,
    InternalType,
    NodeMessage,
    SendMessageAction
} from '../../node.ts';
import { domainForUri, removeGatewayRequest, threadStateAndActions } from '../../state/core.ts';
import { ExceptionInfo, exceptionData, exceptionInfo, exceptionMessage } from '../../common/types.ts';
import { getLogger } from '../../logger.ts';
import * as msg from './messages.ts';
import { instanceId, peerId } from '../../common/ids.ts';
import { byId, byIdentity, byIdThrows, bySource, ensurePeerWithId, removePeer } from '../../state/peers.ts';
import { checkIdentity, machine } from '../../common/peerIdentity.ts';
import { forAuthentication } from '../../common/tokens.ts';
import * as ctx from '../../common/context/ops.ts';
import type { BroadcastDestroy, ContextOverrides } from '../../common/context/ops.ts';
import type { Peer, State } from '../../state/types/state.ts';
import type { DomainInfo } from '../../state/types/domain-registry.ts';
import type { CreateToken, DomainJoin, Hello, Join, Leave, RemoteJoin, WelcomeOptions } from './types/messages.ts';
import type { Authenticator } from '../../auth/core.ts';
import type { Identity, LocalAddress, NodeAddress, PeerId, Source } from '../../state/types/common.ts';
import { peerToAddress } from '../../address.ts';
import type {
    CreateContext,
    DestroyContext,
    SubscribeContext,
    UnsubscribeContext,
    UpdateContext
} from '../../common/context/types/messages.ts';
import { contextDomainUri } from '../../constants.ts';
import type { HelloRequest, JoinRequest } from './types/requests.ts';
import type { MessageReceiver } from '../../common/channel.ts';
import type { PartialBy } from '../../common/utilities.ts';
import { IOGateway } from '../../../gateway';
import { ping } from './messages.ts';
// import {announceContexts} from "../../common/context/ops";

export type Authenticators = { 'default': string, available: { [keys: string]: Authenticator } };

export type RequestSource = {
    request_id: string
    // todo remote identity?
    identity: Identity
}

export type Authenticated = IOGateway.Message<'internal/authenticated'> & AuthenticationData & RequestSource
export type AuthenticationContinue =
    IOGateway.Message<'internal/authentication-request'>
    & AuthenticationData
    & RequestSource
export type AuthenticationError = IOGateway.Message<'internal/authentication-failed'> & RequestSource & {
    message: string
}

export function handleHello(state: State, source: Source, request: HelloRequest, authenticators: Authenticators, handler: MessageReceiver<NodeMessage<InternalType>>): HandleReturnType {
    const {request_id: requestId, identity: remoteIdentity, authentication, options} = request;
    const requestedProvider: string = authentication?.provider ?? authenticators['default'];
    const authenticator = authenticators.available[requestedProvider];
    if (authenticator) {
        const authenticationRequest: AuthenticationRequest = {
            requestId,
            remoteIdentity,
            authentication,
            signatureKey: state.signatureKey,
            clientAuth: source['auth']
        };
        authenticator.authenticate(authenticationRequest)
            .then((msg: AuthenticationResponse) => {
                let type = msg.type as InternalType;
                if (msg.type === 'success') {
                    type = 'internal/authenticated';
                }
                else if (msg.type === 'continue') {
                    type = 'internal/authentication-request';
                }

                return {...msg, type} as IOGateway.Message<InternalType>;
            })
            .catch((err) => {
                log.debug(`authentication request rejected`, err);
                return {
                    message: exceptionMessage(err),
                    ...exceptionData(err),
                    type: 'internal/authentication-failed'
                } as IOGateway.Message<InternalType>;
            }).then((msg: IOGateway.Message<InternalType>) => {
                // add request source
                const m = {...msg, request_id: requestId, identity: remoteIdentity};
                if (m.type === 'internal/authenticated') {
                    (m as Authenticated).options = options;
                }
                const internal: NodeMessage<InternalType> = {
                    origin: 'local',
                    source,
                    body: m
                };
                handler(internal);
            });
        return [state, []];
    } else {
        return [state, [error(globalDomainUri,
            source,
            requestId,
            undefined,
            reason(globalAuthenticationFailure, `Requested authentication provider ${requestedProvider} is not available`))]]
    }
}

function remoteJoin(state: State, source: Source, request: RemoteJoin): HandleReturnType {
    const {peer_id: peerId, identity, options} = request;
    const destinationDomain = domainForUri(state, request.destination);
    if (destinationDomain) {
        [state,] = ensurePeerWithId(state, source, peerId, identity, null, options);
        if (log.enabledFor('info')) {
            log.info(`added remote peer ${peerId} on ${JSON.stringify(source)} with ${JSON.stringify(identity)}`);
        }
        return destinationDomain.handleMessage(state, {
            origin: 'cluster',
            source: source,
            body: {...request, type: 'domain/join'}
        });
    }
    return [state, []];
}

function localJoin(state: State, source: Source, request: JoinRequest): HandleReturnType {

    const {peer_id: peerId, request_id: requestId, destination: domainUri} = request;

    const peer = byId(state, peerId);
    if (peer?.identity) {
        const destinationDomain = domainForUri(state, domainUri);
        if (destinationDomain) {
            const body: DomainJoin = {
                ...request,
                type: 'domain/join',
                identity: peer.identity
            };
            return destinationDomain.handleMessage(state, {
                origin: 'local',
                source: source,
                body
            })
        } else {
            return [state, [error(globalDomainUri, source, requestId, peerId, reason(globalInvalidDomain, `Unable to join missing domain ${domainUri}`))]];
        }
    } else {
        return [state, [error(globalDomainUri, source, requestId, peerId, reason(globalInvalidPeer, `Unable to find peer with id ${peerId}`))]];
    }
}

function forwardJoin(state: State, source: Source, request: JoinRequest): HandleReturnType {
    if (isRemoteSource(source)) {
        return remoteJoin(state, source, request as RemoteJoin);
    } else {
        return localJoin(state, source, request);
    }
}

function contextCompatibility(state: State, source: Source, request): HandleReturnType {
    return localJoin(state, source, request);
}

function remoteLeave(state: State, source: Source, request: Leave): HandleReturnType {
    const {peer_id: peerId, destination: domainUri} = request;
    if (byId(state, peerId)) {
        const destinationDomain = domainForUri(state, domainUri);
        if (destinationDomain) {
            return destinationDomain.handleMessage(state, {
                origin: 'local',
                source: source,
                body: {...request, type: 'domain/leave'}
            });
        }
    }
    return [state, []];
}

function localLeave(state: State, source: Source, request: Leave): HandleReturnType {

    const {peer_id: peerId, request_id: requestId, destination: domainUri} = request;

    if (byId(state, peerId)) {
        const destinationDomain = domainForUri(state, domainUri);
        if (destinationDomain) {
            return destinationDomain.handleMessage(state, {
                origin: 'local',
                source: source,
                body: {...request, type: 'domain/leave'}
            });
        } else {
            return [state, [error(globalDomainUri, source, requestId, peerId, reason(globalInvalidDomain, `Unable to join missing domain ${domainUri}`))]];
        }
    } else {
        return [state, [error(globalDomainUri, source, requestId, peerId, reason(globalInvalidPeer, `Unable to find peer with id ${peerId}`))]];
    }
}

function forwardLeave(state: State, source: Source, request: Leave): HandleReturnType {
    return isRemoteSource(source) ? remoteLeave(state, source, request) : localLeave(state, source, request);
}


const log = getLogger('gateway.domains.global');

export function sourceRemoved(state: State, source: Source, command: IOGateway.Message<"commands/source-removed">): HandleReturnType {
    if (log.enabledFor('debug')) {
        log.debug(`removing source ${JSON.stringify(source)} from global domain`);
    }

    const nodeId = state.ids.nodeId;
    const [newState, msgs] = bySource(state, source).reduce(([s, m], p: Peer) => {
        s = removePeer(s, p);
        if (isLocalSource(source)) {
            if (log.enabledFor('info')) {
                log.info(`removed peer ${p.id} was on endpoint ${source['endpoint']}`);
            }
            m = m.concat(broadcast(peerToAddress(nodeId, p.id), command))
        }
        return [s, m];
    }, [state, []] as HandleReturnType);

    if (log.enabledFor('debug')) {
        log.debug(`removed source ${JSON.stringify(source)} from global domain`);
    }
    // todo measurements
    return [newState, msgs];
}

function resolveQMarks(identity: Record<string, unknown>, peerId: PeerId): Record<string, unknown> {
    return Object
        .keys(identity)
        .filter((key) => identity[key] === '?')
        .reduce((unresolved, key) => {
            const resolved = {};
            resolved[key] = peerId;
            return Object.assign(unresolved, resolved);
        }, identity);
}

/*The gateway token authentication allows a peer to assume part (currently the user) of the
  identity of the peer that has requested its creation*/
function impersonate(remoteIdentity: Identity, impersonateIdentity?: {user: string}) {
    const user = impersonateIdentity?.user;
    if (user) {
        return {...remoteIdentity, user};
    }
    return remoteIdentity;
}

function authenticationFailed(state: State, source: Source, request: AuthenticationError): HandleReturnType {
    const {request_id: requestId, message: msg} = request;
    return [state, [error(globalDomainUri, source, requestId, undefined, reason(globalAuthenticationFailure, msg))]];
}

function authenticationRequest(state: State, source: LocalAddress, request: AuthenticationContinue): HandleReturnType {
    const {request_id: requestId, authentication} = request;
    return [state, [msg.authenticationRequest(globalDomainUri, source, requestId, undefined, authentication)]];
}

function checkExisting(state: State, source: Source, identity: Identity, requestId?: string) {
    const existingId = byIdentity(state, identity);
    if (existingId) {
        const existing = byId(state, existingId);
        throw new ExceptionInfo(
            `peer for ${JSON.stringify(identity)} already accepted on source ${JSON.stringify(existing?.source)} with id ${existingId}, ignoring request (with id?: ${requestId}) on source ${JSON.stringify(source)}`,
            {uri: globalAlreadySeen, message: 'Hello already received once', existing}
        );
    }
}

export function authenticated(state: State, source: LocalAddress, authenticated: PartialBy<Authenticated, 'type'>, settings?: GlobalDomainOptions): HandleReturnType {
    const {
        request_id: requestId,
        identity: remoteIdentity,
        user,
        login,
        impersonatePeer,
        gwRequest,
        accessToken
    } = authenticated;
    if (gwRequest?.id) {
        state = removeGatewayRequest(state, gwRequest.id).state;
    }
    let resolvedIdentity: Record<string, unknown> = {
        machine: machine(source.host, '127.0.0.1'),
        ...remoteIdentity
    };
    if (user) {
        resolvedIdentity = {...resolvedIdentity, user};
    }
    if (login) {
        resolvedIdentity = {...resolvedIdentity, login};
    }
    if (impersonatePeer) {
        resolvedIdentity = impersonate(resolvedIdentity, impersonatePeer)
    }
    if (!resolvedIdentity.instance) {
        const [ids, instance] = instanceId(state.ids);
        resolvedIdentity = {...resolvedIdentity, instance};
        state = {...state, ids}
    }
    const options = {...authenticated.options};
    if (!('context_compatibility_mode' in options)) {
        options.context_compatibility_mode = settings?.contextCompatibility ?? true;
    }
    options["context-compatibility-mode?"] = options.context_compatibility_mode;
    delete options.context_compatibility_mode;

    try {
        checkExisting(state, source, resolvedIdentity, requestId);
        checkIdentity(resolvedIdentity)
        //todo limits

        const [ids, id] = peerId(state.ids);
        resolvedIdentity = resolveQMarks(resolvedIdentity, id);
        let peer;
        [state, peer] = ensurePeerWithId({
            ...state,
            ids
        }, source, id, resolvedIdentity, gwRequest, options);
        if (log.enabledFor('info')) {
            log.info(`added local peer ${peer.id} on endpoint ${source.endpoint} with ${JSON.stringify(resolvedIdentity)}`);
        }
        const welcomeOptions: WelcomeOptions = {};
        if (accessToken) {
            welcomeOptions.access_token = accessToken;
        }
        if (settings?.welcomeInfo) {
            welcomeOptions.info = settings?.welcomeInfo;
        }
        const availableDomains = Object.values<{ info: DomainInfo }>(state.registeredDomains ?? {})
            .map((domain) => domain.info);

        const sendWelcome = msg.welcome(source,
            requestId,
            peer.id,
            availableDomains,
            resolvedIdentity,
            Object.keys(welcomeOptions).length === 0 ? undefined : welcomeOptions);
        return threadStateAndActions(state,
            s => [s, [sendWelcome]],
            // s => announceContexts(globalDomainUri, s, peer),
            s => options["context-compatibility-mode?"] ? contextCompatibility(s, source, {
                request_id: requestId,
                peer_id: peer.id,
                identity: resolvedIdentity,
                options,
                destination: contextDomainUri,
                domain: globalDomainUri
            }) : [s, []]);
    } catch (e) {
        const messages: SendMessageAction<'ping' | 'error'>[] = [];
        const data = exceptionData(e);
        log.warn(`authenticated peer not welcomed`, e);
        if (data?.['uri'] === globalAlreadySeen) {
            const existing = data['existing'] as Peer;
            if (existing) {
                messages.push(ping(existing.source));
            }
        }
        if (isLocalSource(source)) {
            messages.push(error(globalDomainUri, source, requestId, undefined, exceptionToReason(e, failure)));
        }
        return [state, messages];
    }
}

export function forwardUpdate(state: State, source: Source, request: UpdateContext): HandleReturnType {
    return ctx.forwardUpdate(globalDomainUri, state, source, request);
}

export function forwardCreate(state: State, source: Source, request: Omit<CreateContext, 'type'>, options?: ContextOverrides): HandleReturnType {
    return ctx.forwardCreate(globalDomainUri, state, source, request, options);
}

export function forwardDestroy(state: State, source: Source, request: DestroyContext | BroadcastDestroy): HandleReturnType {
    return ctx.forwardDestroy(globalDomainUri, state, source, request);
}

export function forwardSubscribe(state: State, source: Source, request): HandleReturnType {
    return ctx.forwardSubscribe(globalDomainUri, state, source, request);
}

export function forwardUnsubscribe(state: State, source: Source, request: UnsubscribeContext): HandleReturnType {
    return ctx.forwardUnsubscribe(globalDomainUri, state, source, request);
}

export function handleRequest(state: State, source: Source, request: IOGateway.Message<GlobalMessageType>, authenticators: Authenticators, options?: GlobalDomainOptions): HandleReturnType {
    switch (request.type) {
        case 'hello': {
            return handleHello(state, source, request as Hello, authenticators, state.handler!);
        }
        case 'join': {
            return forwardJoin(state, source, request as Join);
        }
        case 'leave': {
            return forwardLeave(state, source, request as Leave);
        }
        case 'internal/authenticated': {
            return authenticated(state, source as LocalAddress, request as Authenticated, options);
        }
        case 'internal/authentication-failed': {
            return authenticationFailed(state, source, request as AuthenticationError);
        }
        case 'internal/authentication-request': {
            return authenticationRequest(state, source as LocalAddress, request as AuthenticationContinue);
        }
        case 'create-context': {
            return forwardCreate(state, source, request as CreateContext, options);
        }
        case 'update-context': {
            return forwardUpdate(state, source, request as UpdateContext);
        }
        case 'subscribe-context': {
            return forwardSubscribe(state, source, request as SubscribeContext);
        }
        case 'unsubscribe-context': {
            return forwardUnsubscribe(state, source, request as UnsubscribeContext);
        }
        case 'destroy-context': {
            return forwardDestroy(state, source, request as DestroyContext);
        }
        case 'ping': {
            return [state, []];
        }
        case 'commands/source-removed': {
            return sourceRemoved(state, source, request as IOGateway.Message<'commands/source-removed'>);
        }
        case 'create-token': {
            const {request_id, peer_id} = (request as CreateToken);
            const peer = byIdThrows(state, peer_id);
            if (peer.identity.user) {
                // todo ttl
                return [state, [token(globalDomainUri, source, request_id, peer_id, forAuthentication(state, {user: peer.identity.user}))]];
            }
            throw exceptionInfo('Cannot create token for peer without user', {});
        }
        default: {
            const message = `Unhandled message ${JSON.stringify(request)}`;
            log.error(message);
            return [state, [error(globalDomainUri, source, request['request_id'] ?? -1, request['peer_id'], reason(globalUnhandledMessage, message))]]
        }
    }
}

type GlobalMessageType = GlobalRequestType | InternalType | CommandType | DomainType;

function handleMessage(state: State,
                       msg: NodeMessage<GlobalMessageType>,
                       authenticators: Authenticators,
                       options: GlobalDomainOptions): HandleReturnType {
    const {source, body} = msg;

    try {
        return handleRequest(state, source, body, authenticators, options);
    } catch (e) {
        return [state, [error(globalDomainUri, source, body['request_id'] ?? -1, body['peer_id'], exceptionToReason(e, failure))]];
    }
}

class GlobalDomain implements Domain<GlobalRequestType> {
    constructor(private readonly authenticators: Authenticators,
                private readonly options: GlobalDomainOptions) {
    }

    info() {
        return {
            uri: globalDomainUri,
            description: '',
            version: 2
        }
    }

    init(state: State) {
        return state;
    }

    destroy(state: State) {
        return state;
    }

    handleMessage(state: State, request: NodeMessage<GlobalRequestType | InternalType | CommandType | DomainType>): HandleReturnType {

        return handleMessage(state, request, this.authenticators, this.options);
    }

    stateToMessages(_state: State, _receiver: NodeAddress): StateToMessagesReturnType {
        return [];
    }
}

export type GlobalDomainOptions = {
    welcomeInfo?: Record<string, unknown>,
    contextCompatibility?: boolean
} & ContextOverrides

export function globalDomain(authenticators: Authenticators, options?: GlobalDomainOptions): GlobalDomain {

    return new GlobalDomain(authenticators, options ?? {contextCompatibility: true});
}
